/**
 * Token Service
 * API calls for token-related operations
 */

import { apiClient as api } from './apiClient';
import {
    TokenStats,
    TokenTransaction,
    TokenPackage,
    TokenStatsResponse,
    TokenTransactionsResponse,
    EarnTokensResponse,
    SpendTokensResponse,
    TokenPackagesResponse,
    UnlockableFeature,
    AdvancedFeature,
    TokenTransactionCategory,
    FeatureStatusMap,
    AdNonceResponse,
} from '../types/tokens';

/**
 * Get current user's token balance and statistics
 */
export async function getTokenStats(): Promise<TokenStats> {
    const response = await api.get<TokenStatsResponse>('/tokens/me');
    return response.data.data;
}

/**
 * Get current user's token transaction history
 */
export async function getTokenTransactions(options?: {
    limit?: number;
    offset?: number;
    category?: TokenTransactionCategory;
}): Promise<{
    transactions: TokenTransaction[];
    pagination: { total: number; limit: number; offset: number; hasMore: boolean };
}> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.category) params.append('category', options.category);

    const response = await api.get<TokenTransactionsResponse>(
        `/tokens/me/transactions?${params.toString()}`
    );
    return {
        transactions: response.data.data,
        pagination: response.data.pagination,
    };
}

/**
 * Get ad nonce (must be called before showing ad).
 *
 * When featureType is supplied, the nonce is bound to that feature so the
 * SSV callback can unlock the correct tier-2 feature on the backend.
 * Also returns the IMA adPolicy flags (tfcd/tfua/npa) derived from the
 * user's ageGroup for COPPA / GDPR-K compliance.
 */
export async function getAdNonce(
    featureType?: FreemiumFeatureType,
): Promise<{ nonce: string; adPolicy?: { tfcd: 0 | 1; tfua: 0 | 1; npa: 0 | 1 } }> {
    const response = await api.post<AdNonceResponse>(
        '/tokens/ad-nonce',
        featureType ? { featureType } : {},
    );
    return response.data.data;
}

/**
 * Earn tokens from watching a rewarded ad (requires nonce from getAdNonce)
 */
export async function earnFromAd(adNonce: string): Promise<{ earned: number; newBalance: number }> {
    const response = await api.post<EarnTokensResponse>('/tokens/earn/ad', { adNonce });
    return response.data.data;
}

/**
 * Get feature usage status for all features
 */
export async function getFeatureStatus(): Promise<FeatureStatusMap> {
    const response = await api.get<{ success: boolean; data: FeatureStatusMap }>('/tokens/feature-status');
    return response.data.data;
}

/**
 * Claim daily login bonus
 */
export async function claimDailyLogin(): Promise<{
    earned: number;
    newBalance: number;
    streak: number;
    streakBonus?: number;
}> {
    const response = await api.post<EarnTokensResponse>('/tokens/earn/daily-login', {});
    return response.data.data as {
        earned: number;
        newBalance: number;
        streak: number;
        streakBonus?: number;
    };
}

/**
 * Unlock a basic feature using tokens
 */
export async function unlockFeature(
    featureType: UnlockableFeature,
    referenceId?: string
): Promise<{ unlocked: boolean; tokensSpent: number; newBalance: number }> {
    const response = await api.post<SpendTokensResponse>('/tokens/spend/unlock', {
        featureType,
        referenceId,
    });
    return response.data.data as { unlocked: boolean; tokensSpent: number; newBalance: number };
}

/**
 * Spend tokens on advanced feature
 */
export async function spendOnAdvancedFeature(
    featureType: AdvancedFeature,
    referenceId?: string
): Promise<{ tokensSpent: number; newBalance: number }> {
    const response = await api.post<SpendTokensResponse>('/tokens/spend/advanced', {
        featureType,
        referenceId,
    });
    return response.data.data;
}

/**
 * Get available token packages for purchase
 */
export async function getTokenPackages(): Promise<TokenPackage[]> {
    const response = await api.get<TokenPackagesResponse>('/tokens/packages');
    return response.data.data;
}

// ============================================
// FREEMIUM ENDPOINTS
// ============================================

export type FreemiumFeatureType =
    | 'vocab_flashcard'
    | 'dictation'
    | 'comprehension_eval'
    | 'practice_materials'
    | 'shadowing'
    | 'quiz'
    | 'writing'
    | 'ai_question'
    | 'learning_progress'
    | 'notes'
    | 'reference';

export interface FreemiumFeatureStatus {
    tier: 1 | 2 | 3;
    usedToday: number;
    dailyLimit: number;
    adUnlocked: boolean;
}

export interface FreemiumStatusResponse {
    isSubscriber: boolean;
    features: Record<string, FreemiumFeatureStatus>;
}

/**
 * Get freemium status (all features, single call)
 */
export async function getFreemiumStatus(): Promise<FreemiumStatusResponse> {
    const response = await api.get<{ success: boolean; data: FreemiumStatusResponse }>('/tokens/freemium-status');
    return response.data.data;
}

/**
 * Unlock a Tier 2 feature via ad nonce
 */
export async function adUnlock(featureType: FreemiumFeatureType, adNonce: string): Promise<{ unlocked: boolean }> {
    const response = await api.post<{ success: boolean; data: { unlocked: boolean } }>('/tokens/ad-unlock', {
        featureType,
        adNonce,
    });
    return response.data.data;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Format token amount with + or - prefix
 */
export function formatTokenAmount(amount: number): string {
    if (amount > 0) return `+${amount}`;
    return amount.toString();
}

/**
 * Get transaction type display name (localized)
 */
export function getTransactionTypeName(
    type: string,
    lang = 'zh-TW'
): string {
    const names: Record<string, Record<string, string>> = {
        ad_reward: { 'zh-TW': '觀看廣告獎勵', en: 'Ad Reward' },
        daily_login: { 'zh-TW': '每日登入獎勵', en: 'Daily Login' },
        daily_goal: { 'zh-TW': '完成每日目標', en: 'Daily Goal' },
        streak_weekly: { 'zh-TW': '7日連勝獎勵', en: '7-Day Streak' },
        streak_monthly: { 'zh-TW': '30日連勝獎勵', en: '30-Day Streak' },
        referral_signup: { 'zh-TW': '好友註冊獎勵', en: 'Referral Signup' },
        referral_subscribe: { 'zh-TW': '好友訂閱獎勵', en: 'Referral Subscribe' },
        subscriber_monthly: { 'zh-TW': '訂閱會員月度獎勵', en: 'Subscriber Bonus' },
        event_reward: { 'zh-TW': '活動獎勵', en: 'Event Reward' },
        admin_grant: { 'zh-TW': '系統發放', en: 'Admin Grant' },
        purchase: { 'zh-TW': '購買代幣', en: 'Purchase' },
        unlock_course: { 'zh-TW': '解鎖課程', en: 'Unlock Course' },
        unlock_dictation: { 'zh-TW': '解鎖聽寫模式', en: 'Unlock Dictation' },
        unlock_shadowing: { 'zh-TW': '解鎖跟讀模式', en: 'Unlock Shadowing' },
        unlock_quiz: { 'zh-TW': '解鎖測驗模式', en: 'Unlock Quiz' },
        unlock_vocabulary: { 'zh-TW': '擴充生字本', en: 'Unlock Vocabulary' },
        ai_analysis: { 'zh-TW': 'AI 發音分析', en: 'AI Analysis' },
        ai_question: { 'zh-TW': 'AI 問答', en: 'AI Question' },
        learning_report: { 'zh-TW': '學習報告', en: 'Learning Report' },
        streak_freeze: { 'zh-TW': '連勝保護盾', en: 'Streak Freeze' },
        leaderboard_boost: { 'zh-TW': '排行榜加成', en: 'Leaderboard Boost' },
        exclusive_content: { 'zh-TW': '獨家內容', en: 'Exclusive Content' },
        avatar_item: { 'zh-TW': '頭像裝飾', en: 'Avatar Item' },
        refund: { 'zh-TW': '退款', en: 'Refund' },
    };
    return names[type]?.[lang] || names[type]?.en || type;
}

/**
 * Check if user can afford a purchase
 */
export function canAfford(balance: number, cost: number): boolean {
    return balance >= cost;
}

/**
 * Calculate discounted price for subscribers
 */
export function calculateSubscriberPrice(
    basePriceCents: number,
    discountPercent = 20
): number {
    return Math.ceil(basePriceCents * (1 - discountPercent / 100));
}

/**
 * Format price in cents to display format
 */
export function formatPrice(cents: number, currency = 'TWD'): string {
    const amount = cents / 100;
    if (currency === 'TWD') {
        return `NT$${Math.round(amount)}`;
    }
    return `$${amount.toFixed(2)}`;
}
